// The Ouro Code — community conduct manifesto shown once per browser
// until the copy version changes.
//
// Bump OURO_CODE_VERSION whenever the copy below changes. A version mismatch
// invalidates a prior acknowledgement and forces players to re-read and re-accept.

export const OURO_CODE_VERSION = 1;

export const OURO_CODE_INTRO =
  "Ouro is the competitive scene we wish existed. These are the things we don't compromise on.";

export const OURO_CODE_PRINCIPLES = [
  {
    title: "Mic on.",
    body: "Hop on voice when you queue - comms are part of the game here, not optional.",
  },
  {
    title: "English in comms.",
    body: "One shared language so every callout lands.",
  },
  {
    title: "Respect everyone.",
    body: "No discrimination, full stop - regardless of gender, age, nationality, or rank.",
  },
  {
    title: "Keep it clean.",
    body: "No slurs, no hate, no politics. Leave it at the door.",
  },
  {
    title: "Lift people up.",
    body: "Stay positive and friendly, especially with newer players — we all started somewhere.",
  },
];

const STORAGE_KEY = "ouro.ouroCodeAck.v1";
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365 * 10;

// In-memory fallback so acknowledgement survives even when storage writes fail.
let memoryState = null;

function parseState(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function readFromStorage(storage) {
  if (!storage) return null;
  try {
    return parseState(storage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeToStorage(storage, raw) {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, raw);
  } catch {
    // Storage unavailable — other layers may still hold the ack.
  }
}

function readFromCookie() {
  if (typeof document === "undefined" || typeof document.cookie !== "string") {
    return null;
  }
  try {
    const prefix = `${STORAGE_KEY}=`;
    const found = document.cookie
      .split("; ")
      .find((part) => part.startsWith(prefix));
    if (!found) return null;
    return parseState(decodeURIComponent(found.slice(prefix.length)));
  } catch {
    return null;
  }
}

function writeCookie(raw) {
  if (typeof document === "undefined") {
    return;
  }
  try {
    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(raw)}; path=/; max-age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
  } catch {
    // Cookie unavailable — other layers may still hold the ack.
  }
}

function readState() {
  if (memoryState && typeof memoryState === "object") {
    return memoryState;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const state =
    readFromStorage(window.localStorage) ||
    readFromStorage(window.sessionStorage) ||
    readFromCookie();

  if (state) {
    memoryState = state;
  }
  return state;
}

function writeState(state) {
  memoryState = state;
  const raw = JSON.stringify(state);

  if (typeof window === "undefined") {
    return;
  }

  writeToStorage(window.localStorage, raw);
  writeToStorage(window.sessionStorage, raw);
  writeCookie(raw);
}

function isAckedState(state) {
  if (!state || typeof state !== "object") return false;
  if (Number(state.version) !== OURO_CODE_VERSION) return false;
  return state.acked === true || state.acked === 1 || state.acked === "true";
}

export function shouldShowOuroCode() {
  return !isAckedState(readState());
}

export function recordOuroCodeAck() {
  writeState({
    version: OURO_CODE_VERSION,
    acked: true,
    lastAckAt: Date.now(),
  });
}

export function resetOuroCodeAckForTests() {
  memoryState = null;
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
